import { FC, useEffect, useMemo } from 'react'
import { Tab, TabList, TabPanel, TabPanels, Tabs, Icon } from '@chakra-ui/react'
import { useSearchParams } from 'react-router-dom'
import {
  FiUser,
  FiStar,
  FiUsers,
  FiBriefcase,
  FiMapPin,
  FiFolder,
  FiActivity,
} from 'react-icons/fi'
import Page from '../../components/Page'
import PrescriberProfilePage from '../PrescriberProfilePage/PrescriberProfilePage'
import OrganizationProfilePage from '../OrganizationProfilePage'
import { SubscriptionInfo } from '../Billing/SubscriptionInfo'
import { RootState } from '../../app/store'
import { useAppSelector } from '../../app/typedReduxHooks'
import { OrganizationAdminProfilePage } from '../../features/organization-admin/OrganizationAdminProfilePage'
import { UsersPage } from '../../features/users/UsersPage'
import UnauthorizedPage from '../../components/UnauthorizedPage'
import { IntegrationsPage } from '../../features/integration/IntegrationsPage'
import { LocationsPage } from '../../features/locations/LocationsPage'
import { useFeatureToggle } from '../../common/utils/feature-toggle/useFeatureToggle'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { PlanCode } from '../../types/subscription'
import { isOwnerOrSuperAdminCapable } from '../../features/users/utils/organizationAdminCapabilities'

import { IconType } from 'react-icons'

interface TabConfig {
  id: string
  label: string
  component: React.ReactNode
  icon: IconType
  isVisible: boolean
}

const SettingsPage: FC<unknown> = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const authInfo = useAppSelector((state: RootState) => state.authentication)
  const loginFinished = authInfo.loginFinished
  const permissions = authInfo?.permissions ?? []
  const organizationId = useAppSelector((state: RootState) => state.organization?.data?.id)
  const organizationSettings = useAppSelector((state) => state.organization?.data?.settings)
  const isPrescriberActive = useAppSelector((state) => state.prescriber.prescriber?.active)
  const organizationAdmin = useAppSelector((state) => state.organizationAdmin.profile)
  const isAdminActive = organizationAdmin?.active
  const subscription = useAppSelector((state: RootState) => state.billing?.subscription.data)
  const organizationPlanCode = useAppSelector(
    (state: RootState) => state.organization?.entitlements?.planCode
  )

  const isOwnerOrSuperAdmin = isOwnerOrSuperAdminCapable(organizationAdmin)

  const isAbleToManageBilling =
    subscription?.stripeSubscriptionId &&
    organizationPlanCode !== PlanCode.UNLIMITED_ONE_YEAR &&
    isOwnerOrSuperAdmin

  const { isFeatureEnabled: isIntegrationsFeatureOn } = useFeatureToggle('INTEGRATIONS')
  const hasMultipleLocations = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)

  const tabConfigs: TabConfig[] = useMemo(
    () => [
      {
        id: 'prescriber',
        label: 'Prescriber Profile',
        component: <PrescriberProfilePage />,
        icon: FiUser,
        isVisible: loginFinished && !!isPrescriberActive,
      },
      {
        id: 'admin',
        label: 'Admin Profile',
        component: <OrganizationAdminProfilePage />,
        icon: FiStar,
        isVisible: loginFinished && !!isAdminActive,
      },
      {
        id: 'users',
        label: 'Users',
        component: <UsersPage />,
        icon: FiUsers,
        isVisible: loginFinished && !!organizationSettings?.enableUsersBoard,
      },
      {
        id: 'organization',
        label: 'Organisation',
        component: <OrganizationProfilePage />,
        icon: FiBriefcase,
        isVisible: loginFinished && !!organizationSettings?.enableUpdateOrganization,
      },
      {
        id: 'locations',
        label: 'Locations',
        component: <LocationsPage />,
        icon: FiMapPin,
        isVisible: loginFinished && hasMultipleLocations && isOwnerOrSuperAdmin,
      },
      {
        id: 'subscription',
        label: 'Subscription',
        component: <SubscriptionInfo subscription={subscription} />,
        icon: FiFolder,
        isVisible: loginFinished && !!isAbleToManageBilling,
      },
      {
        id: 'integrations',
        label: 'Integrations',
        component: <IntegrationsPage />,
        icon: FiActivity,
        isVisible:
          loginFinished &&
          !!organizationSettings?.enableAutoBuildIntegration &&
          isIntegrationsFeatureOn,
      },
    ],
    [
      loginFinished,
      isPrescriberActive,
      isAdminActive,
      organizationSettings?.enableUsersBoard,
      organizationSettings?.enableUpdateOrganization,
      organizationSettings?.enableAutoBuildIntegration,
      isAbleToManageBilling,
      subscription,
      hasMultipleLocations,
      isOwnerOrSuperAdmin,
      isIntegrationsFeatureOn,
    ]
  )

  const visibleTabs = useMemo(() => tabConfigs.filter((tab) => tab.isVisible), [tabConfigs])

  const currentTab = searchParams.get('tab')
  const currentTabIndex = visibleTabs.findIndex((tab) => tab.id === currentTab)

  const handleTabChange = (index: number) => {
    const selectedTab = visibleTabs[index]
    if (selectedTab) {
      setSearchParams({ tab: selectedTab.id })
    }
  }

  useEffect(() => {
    if (currentTabIndex === -1 && visibleTabs.length > 0) {
      const requestedTab = searchParams.get('tab')

      const handleTabWithLoadingDependencies = (tabId: string) => {
        if (tabId === 'subscription') {
          if (!organizationAdmin && loginFinished) {
            return true
          }

          if (!organizationId && loginFinished) {
            return true
          }

          const hasBillingPermission = isOwnerOrSuperAdminCapable(organizationAdmin)

          if (hasBillingPermission && organizationId && !subscription) {
            return true
          }

          if (hasBillingPermission && subscription?.stripeSubscriptionId) {
            return true
          }
        }

        if (['users', 'organization', 'locations', 'integrations'].includes(tabId)) {
          if (!organizationId && loginFinished) {
            return true
          }

          if (!organizationSettings && loginFinished && organizationId) {
            return true
          }

          const tabShouldBeVisible = {
            users: organizationSettings?.enableUsersBoard,
            organization: organizationSettings?.enableUpdateOrganization,
            integrations: organizationSettings?.enableAutoBuildIntegration,
            locations: hasMultipleLocations && isOwnerOrSuperAdmin,
          }[tabId]

          if (tabShouldBeVisible) {
            return true
          }
        }

        return false
      }

      if (requestedTab && handleTabWithLoadingDependencies(requestedTab)) {
        return
      }

      const defaultTab =
        visibleTabs.find((tab) => ['prescriber', 'admin'].includes(tab.id)) || visibleTabs[0]

      if (defaultTab) {
        setSearchParams({ tab: defaultTab.id })
      }
    }
  }, [
    currentTabIndex,
    visibleTabs,
    setSearchParams,
    searchParams,
    subscription,
    organizationId,
    organizationAdmin,
    organizationSettings,
    loginFinished,
    isAbleToManageBilling,
    hasMultipleLocations,
    isOwnerOrSuperAdmin,
  ])

  if (permissions.length === 0) {
    return <UnauthorizedPage />
  }

  const activeTabIndex = Math.max(0, currentTabIndex)

  return (
    <Page>
      <Tabs size="lg" index={activeTabIndex} onChange={handleTabChange} colorScheme="blue">
        <TabList whiteSpace="nowrap" overflowX="auto" overflowY="hidden">
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.id}
              fontWeight="semibold"
              color="gray.800"
              _selected={{
                color: 'blue.600',
                borderBottom: '4px solid',
                borderColor: 'blue.600',
                borderRadius: '4px',
              }}
            >
              <Icon as={tab.icon} boxSize={4} mr={2} />
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {visibleTabs.map((tab) => (
            <TabPanel key={tab.id} px={8} py={4}>
              {tab.component}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Page>
  )
}

export default SettingsPage
